/**
 * Frontend Permissions Utility
 * 
 * This file contains constants and helper functions for managing frontend permissions.
 * Frontend permissions control menu visibility and feature access in the UI.
 */

// Menu Visibility Permissions
export const MENU_PERMISSIONS = {
  DASHBOARD: 'menu:dashboard',
  INVENTORY_IMPORT: 'menu:inventory-import',
  INVENTORY_TRANSFER: 'menu:inventory-transfer',
  INVENTORY_INSPECTION: 'menu:inventory-inspection',
  REPORT: 'menu:report',
  TAG_TIMELINE: 'menu:tag-timeline',
  USER_ROLES_PERMISSIONS: 'menu:user-roles-permissions',
  // Notification Log and Settings are always visible (no permission required)
};

// Feature-level Permissions by Menu
export const FEATURE_PERMISSIONS = {
  // Dashboard Features
  DASHBOARD: {
    VIEW: 'dashboard:view',
  },
  
  // Inventory Import Features
  INVENTORY_IMPORT: {
    CREATE: 'inventory-import:create',
    EDIT: 'inventory-import:edit',
    EDIT_SERIALS: 'inventory-import:edit-serials',
    CONFIRM: 'inventory-import:confirm',
  },
  
  // Inventory Transfer Features
  INVENTORY_TRANSFER: {
    CREATE: 'inventory-transfer:create',
    EDIT_PRODUCTS: 'inventory-transfer:edit-products',
    EDIT_SERIALS: 'inventory-transfer:edit-serials',
    UPDATE_INFO: 'inventory-transfer:update-info',
    CHANGE_PRODUCT: 'inventory-transfer:change-product',
    CHANGE_SERIALS: 'inventory-transfer:change-serials',
    CONFIRM_EXPORT: 'inventory-transfer:confirm-export',
    CONFIRM_IMPORT: 'inventory-transfer:confirm-import',
    CONFIRM_TRANSFER_DATA: 'inventory-transfer:confirm-transfer-data',
    IMPORT_TRANSFER_DATA: 'inventory-transfer:import-transfer-data',
  },
  
  // Inventory Inspection Features
  INVENTORY_INSPECTION: {
    VIEW_HISTORY: 'inventory-inspection:view-history',
  },
  
  // Report Features
  REPORT: {
    VIEW: 'report:view',
  },
  
  // Tag Timeline Features
  TAG_TIMELINE: {
    VIEW: 'tag-timeline:view',
  },
  
  // User, Roles & Permissions Features
  USER_ROLES_PERMISSIONS: {
    VIEW: 'user-roles-permissions:view',
    MANAGE: 'user-roles-permissions:manage',
  },
};

/**
 * Get all menu permissions as an array
 * @returns {string[]} Array of all menu visibility permissions
 */
export const getAllMenuPermissions = () => {
  return Object.values(MENU_PERMISSIONS);
};

/**
 * Get all feature permissions as an array
 * @returns {string[]} Array of all feature permissions
 */
export const getAllFeaturePermissions = () => {
  const allPermissions = [];
  Object.values(FEATURE_PERMISSIONS).forEach(category => {
    allPermissions.push(...Object.values(category));
  });
  return allPermissions;
};

/**
 * Get all permissions (menu + features) as an array
 * @returns {string[]} Array of all permissions
 */
export const getAllPermissions = () => {
  return [...getAllMenuPermissions(), ...getAllFeaturePermissions()];
};

/**
 * Get feature permissions for a specific menu
 * @param {string} menuKey - The menu key (e.g., 'DASHBOARD', 'ORDERS')
 * @returns {string[]} Array of feature permissions for that menu
 */
export const getFeaturePermissionsForMenu = (menuKey) => {
  const features = FEATURE_PERMISSIONS[menuKey];
  return features ? Object.values(features) : [];
};

/**
 * Check if a permission is a menu visibility permission
 * @param {string} permission - The permission to check
 * @returns {boolean} True if it's a menu permission
 */
export const isMenuPermission = (permission) => {
  return permission.startsWith('menu:');
};

/**
 * Check if a permission is a feature permission
 * @param {string} permission - The permission to check
 * @returns {boolean} True if it's a feature permission
 */
export const isFeaturePermission = (permission) => {
  return !permission.startsWith('menu:');
};

export default {
  MENU_PERMISSIONS,
  FEATURE_PERMISSIONS,
  getAllMenuPermissions,
  getAllFeaturePermissions,
  getAllPermissions,
  getFeaturePermissionsForMenu,
  isMenuPermission,
  isFeaturePermission,
};

